import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    CreditCard,
    AlertCircle,
    Calendar,
    User
} from 'lucide-react';
import { useGetAllMembershipsQuery, useUpdateMembershipStatusMutation, UserMembership } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

const EditMembership: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const membershipId = id ? parseInt(id) : null;
    const isValidId = membershipId && !isNaN(membershipId) && membershipId > 0;

    // Fetch all memberships and find the one we need
    const { data: membershipsResponse, isLoading, error } = useGetAllMembershipsQuery({
        page: 1,
        limit: 1000,
    }, {
        skip: !isValidId,
    });

    const [updateMembershipStatus, { isLoading: isUpdating }] = useUpdateMembershipStatusMutation();

    const memberships = membershipsResponse?.data?.memberships || [];
    const membership = memberships.find((m: UserMembership) => m.membership_id === membershipId);

    const [formData, setFormData] = useState({
        status: 'active' as 'active' | 'expired' | 'cancelled'
    });

    const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

    // Initialize form when membership loads
    useEffect(() => {
        if (membership) {
            setFormData({
                status: membership.status
            });
        }
    }, [membership]);

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<string, string>> = {};

        if (!formData.status) {
            newErrors.status = 'Status is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!membership || !validateForm()) {
            return;
        }

        try {
            await updateMembershipStatus({
                membership_id: membership.membership_id,
                status: formData.status
            }).unwrap();

            toast.success('Membership updated successfully!');
            navigate(`/admin/memberships/view/${membership.membership_id}`);
        } catch (error: any) {
            console.error('Failed to update membership:', error);
            toast.error(error?.data?.message || 'Failed to update membership');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading membership details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Membership</h2>
                    <p className="text-gray-600 mb-6">
                        {(error as any)?.data?.message || 'There was an error loading the membership details. Please try again.'}
                    </p>
                    <button
                        onClick={() => navigate('/admin/memberships')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Memberships
                    </button>
                </div>
            </div>
        );
    }

    if (!membership) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Membership Not Found</h2>
                    <p className="text-gray-600 mb-6">The membership you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/memberships')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Memberships
                    </button>
                </div>
            </div>
        );
    }

    // Format date
    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate(`/admin/memberships/view/${membership.membership_id}`)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Edit Membership</h1>
                                <p className="text-gray-600">Membership ID: #{membership.membership_id}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Membership Information Display */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Membership Information
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">User</p>
                                        <p className="text-base font-medium text-gray-900">{membership.user?.name}</p>
                                        <p className="text-sm text-gray-500">{membership.user?.email}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Plan</p>
                                        <p className="text-base font-medium text-gray-900">{membership.plan?.name}</p>
                                        <p className="text-sm text-gray-500">{formatCurrency(membership.plan?.price || 0)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Start Date</p>
                                        <p className="text-base font-medium text-gray-900">{formatDate(membership.start_date)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">End Date</p>
                                        <p className="text-base font-medium text-gray-900">{formatDate(membership.end_date)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Edit Form */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Update Status
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status *
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => handleInputChange('status', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.status ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    >
                                        <option value="active">Active</option>
                                        <option value="expired">Expired</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    {errors.status && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.status}
                                        </p>
                                    )}
                                </div>

                                {/* Information Note */}
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <AlertCircle className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900 mb-1">Note</h4>
                                            <p className="text-xs text-gray-700">
                                                You can update the membership status. Other fields like dates and plan information cannot be modified from here.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex items-center justify-end space-x-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate(`/admin/memberships/view/${membership.membership_id}`)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isUpdating}
                            >
                                {isUpdating ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Update Membership
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditMembership;

